import { dependencies } from '@pn/core/dependencies';
import type { DataItem, DataItemId } from '@pn/core/domain/data';
import type {
  FilterProperty,
  Layer,
  LayerPattern,
  LayerStyle,
} from '@pn/core/domain/layer';
import type { Filter, LinkOperator, Query, Sort } from '@pn/core/domain/query';
import {
  getDataItemSelected,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { createSelector } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';
import React from 'react';
import { useParams } from 'react-router-dom';
import { type State, workspaceSlice } from './workspaceSlice';

export const useWorkspaceStorage = () => {
  const { name, getInitialState } = workspaceSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  const { id } = useParams();
  const selectDataItemSelected = React.useMemo(
    () => makeSelectDataItemSelected(id),
    [id]
  );

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isFetchingStack: useSliceSelector((state) => state.isFetchingStack),
    isStackAccessDenied: useSliceSelector((state) => state.isStackAccessDenied),
    allWorkspaceItems: useSliceSelector((state) => state.allWorkspaceItems),
    workspaceItems: useSliceSelector(selectWorkspaceItems),
    idsInWorkspace: useSliceSelector((state) => state.idsInWorkspace),
    workspaceItemSelected: useSliceSelector(selectWorkspaceItemSelected),
    dataItemSelected: useSliceSelector(selectDataItemSelected),
    invalidDataItemIds: useSliceSelector((state) => state.invalidDataItemIds),

    allAreaWorkspaceItems: useSliceSelector(selectAllAreaWorkspaceItems),
    areaWorkspaceItems: useSliceSelector(selectAreaWorkspaceItems),
  };
};

export const workspaceActions = () => {
  const { dispatch } = dependencies.store;
  const { actions } = workspaceSlice;

  return {
    add: (itemOrItems: WorkspaceItem | WorkspaceItem[], upsert = true) =>
      dispatch(actions.add({ itemOrItems, upsert })),
    create: (item: WorkspaceItem) => dispatch(actions.create(item)),
    update: (item: WorkspaceItem) => dispatch(actions.update(item)),
    overwrite: (item: WorkspaceItem) => dispatch(actions.overwrite(item)),
    remove: (item: WorkspaceItem) => dispatch(actions.remove(item)),
    removeStackOriginatedItems: () =>
      dispatch(actions.removeStackOriginatedItems()),
    removeAreasFromWorkspace: () =>
      dispatch(actions.removeAreasFromWorkspace()),
    share: (item: WorkspaceItem) => dispatch(actions.share(item)),
    unshare: (item: WorkspaceItem) => dispatch(actions.unshare(item)),
    updateIsFetching: (isFetching: boolean) =>
      dispatch(actions.updateIsFetching(isFetching)),
    updateIsFetchingStack: (isFetching: boolean) =>
      dispatch(actions.updateIsFetchingStack(isFetching)),
    updateIsStackAccessDenied: (isDenied: boolean) =>
      dispatch(actions.updateIsStackAccessDenied(isDenied)),
    updateInvalidDataItemIds: (ids: DataItemId[]) =>
      dispatch(actions.updateInvalidDataItemIds(ids)),
    updateQueryProperty: (itemId: WorkspaceItem['id'], query: Partial<Query>) =>
      dispatch(actions.updateQueryProperty({ itemId, query })),
    addLayer: (itemId: WorkspaceItem['id'], layer: Layer) =>
      dispatch(actions.addLayer({ itemId, layer })),
    removeLayer: (itemId: WorkspaceItem['id'], layerId: string) =>
      dispatch(actions.removeLayer({ itemId, layerId })),
    updateFilterPropertyOptions: (
      itemId: WorkspaceItem['id'],
      filterPropertyOptions: FilterProperty['options']
    ) =>
      dispatch(
        actions.updateFilterPropertyOptions({
          itemId,
          filterPropertyOptions,
        })
      ),
    updateLayerStyle: (
      itemId: WorkspaceItem['id'],
      layerId: string,
      style: LayerStyle
    ) => dispatch(actions.updateLayerStyle({ itemId, layerId, style })),
    updateAllLayerStyles: (itemId: WorkspaceItem['id'], style: LayerStyle) =>
      dispatch(actions.updateAllLayerStyles({ itemId, style })),
    updateLayerPattern: (
      itemId: WorkspaceItem['id'],
      layerId: string,
      pattern: LayerPattern | undefined
    ) => dispatch(actions.updateLayerPattern({ itemId, layerId, pattern })),
    overrideLayers: (itemId: WorkspaceItem['id'], layers: Layer[]) =>
      dispatch(actions.overrideLayers({ itemId, layers })),
    revertLayersToOriginal: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.revertLayersToOriginal(itemId)),
    recolorPNSymbolLayers: (color: string) =>
      dispatch(actions.recolorPNSymbolLayers(color)),
    enableBubbleMapping: (
      itemId: WorkspaceItem['id'],
      params: {
        radius: unknown[];
        color?: unknown;
      }
    ) => dispatch(actions.enableBubbleMapping({ itemId, ...params })),
    disableBubbleMapping: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.disableBubbleMapping(itemId)),
    updateNumberOfElements: (
      itemId: WorkspaceItem['id'],
      numberOfElements: number
    ) =>
      dispatch(
        actions.updateNumberOfElements({
          itemId,
          numberOfElements,
        })
      ),
    updateName: (itemId: WorkspaceItem['id'], name: string) =>
      dispatch(actions.updateName({ itemId, name })),
    addToWorkspace: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.addToWorkspace(itemId)),
    removeFromWorkspace: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.removeFromWorkspace(itemId)),
    setWorkspace: (
      itemIds: WorkspaceItem['id'][],
      preserveVisibility?: boolean
    ) =>
      dispatch(actions.setWorkspace({ itemIds: itemIds, preserveVisibility })),
    select: (itemId: WorkspaceItem['id'] | undefined) =>
      dispatch(actions.select(itemId)),

    /* INTERNAL STATE */

    updateCounts: (
      itemId: WorkspaceItem['id'],
      params: {
        totalCount: number;
        streamedCount: number;
      }
    ) => dispatch(actions.updateCounts({ itemId, ...params })),
    updateVisibility: (itemId: WorkspaceItem['id'], isVisible: boolean) =>
      dispatch(actions.updateVisibility({ itemId, isVisible })),
    updateTableDataItems: (
      itemId: WorkspaceItem['id'],
      tableDataItems: DataItem[]
    ) =>
      dispatch(
        actions.updateTableDataItems({
          itemId,
          tableDataItems,
        })
      ),
    markAsLoaded: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.markAsLoaded(itemId)),
    markAsProcessed: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.markAsProcessed(itemId)),
    markAsUnfinalized: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.markAsUnfinalized(itemId)),
    markAsRendered: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.markAsRendered(itemId)),
    markAsError: (itemId: WorkspaceItem['id'], errorMessage?: string) =>
      dispatch(actions.markAsError({ itemId, errorMessage })),
    revisualize: (itemId: WorkspaceItem['id']) =>
      dispatch(actions.revisualize(itemId)),
    resetInternalState: () => dispatch(actions.resetInternalState()),

    /* QUERY ACTIONS */

    updateSorting: (itemId: WorkspaceItem['id'], sorts: Sort[]) =>
      dispatch(actions.updateSorts({ itemId, sorts })),
    updateFiltering: (
      itemId: WorkspaceItem['id'],
      filters: Filter[],
      linkOperator: LinkOperator
    ) => dispatch(actions.updateFiltering({ itemId, filters, linkOperator })),
    updatePage: (itemId: WorkspaceItem['id'], page: number) =>
      dispatch(actions.updatePage({ itemId, page })),
    recalculateAllPages: (
      prevPageSize: number,
      pageSize: number,
      id: DataItemId | undefined
    ) => dispatch(actions.recalculateAllPages({ prevPageSize, pageSize, id })),
    setCheckedIds: (itemId: WorkspaceItem['id'], ids: DataItemId[]) =>
      dispatch(actions.setCheckedIds({ itemId, ids })),
    updateScopeIds: (itemId: WorkspaceItem['id'], ids: DataItemId[]) =>
      dispatch(actions.updateScopeIds({ itemId, ids })),
    updateClickedIds: (itemId: WorkspaceItem['id'], ids: DataItemId[]) =>
      dispatch(actions.updateClickedIds({ itemId, ids })),
  };
};

const selectWorkspaceItems = createSelector(
  [
    (state: State) => state.allWorkspaceItems,
    (state: State) => state.idsInWorkspace,
  ],
  (allWorkspaceItems, idsInWorkspace) =>
    idsInWorkspace.reduce<Record<WorkspaceItem['id'], WorkspaceItem>>(
      (acc, id) => {
        const item = allWorkspaceItems[id];
        if (!isNil(item)) acc[item.id] = item;
        return acc;
      },
      {}
    )
);

const selectWorkspaceItemSelected = createSelector(
  [
    (state: State) => state.allWorkspaceItems,
    (state: State) => state.idsInWorkspace,
    (state: State) => state.workspaceItemIdSelected,
  ],
  (allWorkspaceItems, idsInWorkspace, workspaceItemIdSelected) => {
    return !isNil(workspaceItemIdSelected) &&
      idsInWorkspace.includes(workspaceItemIdSelected)
      ? allWorkspaceItems[workspaceItemIdSelected]
      : undefined;
  }
);

const makeSelectDataItemSelected = (id: string | undefined) =>
  createSelector([selectWorkspaceItemSelected], (workspaceItemSelected) => {
    return !isNil(workspaceItemSelected)
      ? getDataItemSelected(workspaceItemSelected, id)
      : undefined;
  });

const selectAreaWorkspaceItems = createSelector(
  [
    (state: State) => state.allWorkspaceItems,
    (state: State) => state.idsInWorkspace,
  ],
  (allWorkspaceItems, idsInWorkspace) =>
    Object.values(allWorkspaceItems).filter(
      (item) => idsInWorkspace.includes(item.id) && item.metadata?.isQueryArea
    )
);

const selectAllAreaWorkspaceItems = createSelector(
  [(state: State) => state.allWorkspaceItems],
  (allWorkspaceItems) =>
    Object.values(allWorkspaceItems).filter(
      ({ metadata }) => metadata?.isQueryArea
    )
);

// HACK:
export function isWorkspaceItemCleared(itemId: string): boolean {
  const { idsInWorkspace, allWorkspaceItems } = dependencies.store.getState()
    .workspace as State;

  const isInWorkspace = !isNil(idsInWorkspace.find((id) => id === itemId));
  const item = allWorkspaceItems[itemId];
  const isProcessed = item?.isProcessed ?? false;

  return !isInWorkspace && !isProcessed;
}
